import { makeAutoObservable } from "mobx"
import { authenticationApi, resetForceLogout } from "@/lib/api"
import { LoginResponse, LoginUserDTO, LoginUserDTORoleEnum } from "@/gen/api"
import { queryClient } from "@/lib/queryClient"

export type UserRole = LoginUserDTORoleEnum

export type User = Pick<
  LoginUserDTO,
  "id" | "email" | "role" | "councils" | "name" | "permissions"
> & {
  councilId?: number | null
}

export type LoginResult =
  | { type: "authenticated"; user: User }
  | { type: "two_factor"; challengeToken: string; setupRequired: boolean }

class AuthStore {
  user: User | null = this.loadPersistedUser()
  isAuthenticated: boolean = this.user !== null

  private loadPersistedUser(): User | null {
    try {
      const raw = localStorage.getItem("auth-storage")
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return (parsed?.state?.user as User) ?? null
    } catch {
      return null
    }
  }

  constructor() {
    makeAutoObservable(this)
  }

  async login(email: string, password: string): Promise<LoginResult> {
    const userResponse = await authenticationApi.login({ email, password })
    const payload = userResponse.data as { data?: LoginResponse }
    if (payload?.data?.twoFactorRequired) {
      const challengeToken = payload.data.twoFactorChallengeToken
      if (!challengeToken) {
        throw new Error("Login failed: missing two-factor challenge")
      }
      return {
        type: "two_factor",
        challengeToken,
        setupRequired: Boolean(payload.data.twoFactorSetupRequired),
      }
    }
    if (!payload?.data?.user) {
      throw new Error("Login failed: invalid response from server")
    }
    const userData = payload.data.user
    return this.completeLogin(userData)
  }

  completeLogin(userData: LoginUserDTO): LoginResult {
    this.user = userData
    this.isAuthenticated = true
    this.persist()
    resetForceLogout()
    void queryClient.invalidateQueries()
    return { type: "authenticated", user: userData }
  }

  async logout() {
    try {
      await authenticationApi.logout()
    } catch {
      // ignore logout error
    }
    this.user = null
    this.isAuthenticated = false
    this.clearPersist()
    queryClient.clear()
  }

  setUser(user: User) {
    this.user = user
    this.isAuthenticated = true
    this.persist()
  }

  clearUser() {
    this.user = null
    this.isAuthenticated = false
    this.clearPersist()
  }

  handleAuthError(error?: unknown) {
    this.user = null
    this.isAuthenticated = false
    this.clearPersist()
    queryClient.clear()
    if (error && typeof error === "object" && "response" in error) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } }
        message?: string
      }
      console.warn(
        "Authentication error detected, logging out:",
        err.response?.status,
        err.response?.data?.message || err.message,
      )
    }
  }

  updateUserProfile(updates: Partial<User>) {
    if (!this.user) return
    this.user = { ...this.user, ...updates }
    this.persist()
  }

  private persist() {
    try {
      localStorage.setItem(
        "auth-storage",
        JSON.stringify({ state: { user: this.user, isAuthenticated: this.isAuthenticated } }),
      )
    } catch {
      // localStorage not available
    }
  }

  private clearPersist() {
    try {
      localStorage.removeItem("auth-storage")
    } catch {
      // localStorage not available
    }
  }
}

export const authStore = new AuthStore()
